import { useEffect, useRef, useState } from "react";
import { Button, Input, message } from "../ui/antd";
import type { GetRef } from "../ui/antd";
import { ArrowLeftOutlined, ArrowRightOutlined, CheckCircleFilled, LockOutlined, MessageOutlined, MobileOutlined, SafetyCertificateOutlined } from "../ui/icons";
import { api, setToken, type User } from "../api";
import { authClientContext } from "../auth-client";
import RealSmsTestLogin from "./RealSmsTestLogin";
import "./login-studio.css";

type OTPRef = GetRef<typeof Input.OTP>;
type Phase = "phone" | "code";

export default function LoginStudio({ onLogin }: { onLogin: (user: User) => void }) {
  const [realSmsTest, setRealSmsTest] = useState(() => new URLSearchParams(window.location.search).get("login") === "real-sms");
  const [phase, setPhase] = useState<Phase>("phone");
  const [phone, setPhone] = useState("");
  const [code, setCode] = useState("");
  const [countdown, setCountdown] = useState(0);
  const [loading, setLoading] = useState(false);
  const timerRef = useRef<number | null>(null);
  const otpRef = useRef<OTPRef>(null);

  useEffect(() => () => {
    if (timerRef.current !== null) window.clearInterval(timerRef.current);
  }, []);

  useEffect(() => {
    if (countdown <= 0) return undefined;
    timerRef.current = window.setInterval(() => {
      setCountdown((current) => {
        if (current <= 1) {
          if (timerRef.current !== null) window.clearInterval(timerRef.current);
          timerRef.current = null;
          return 0;
        }
        return current - 1;
      });
    }, 1000);
    return () => {
      if (timerRef.current !== null) window.clearInterval(timerRef.current);
    };
  }, [countdown]);

  const resetCode = () => {
    setCode("");
    setCountdown(0);
  };

  const requestCode = async () => {
    if (!/^1\d{10}$/.test(phone)) {
      message.warning("请输入11位手机号");
      return;
    }
    setLoading(true);
    try {
      const result = await api<{ testCode?: string }>("/api/auth/request-code", { method: "POST", body: JSON.stringify({ phone, purpose: "login" }) });
      setCountdown(60);
      setPhase("code");
      if (result.testCode) {
        setCode(result.testCode);
        message.success(`内部试运行验证码：${result.testCode}`);
      } else {
        message.success("验证码已发送");
      }
      window.setTimeout(() => otpRef.current?.focus(), 100);
    } catch (error) {
      message.error(error instanceof Error ? error.message : "发送失败");
    } finally {
      setLoading(false);
    }
  };

  const verifyCode = async (nextCode = code) => {
    const verificationCode = nextCode.trim();
    if (!/^\d{6}$/.test(verificationCode)) {
      message.warning("请输入6位验证码");
      return;
    }
    setLoading(true);
    try {
      const result = await api<{ token: string; user: User }>("/api/auth/verify-code", {
        method: "POST",
        body: JSON.stringify({ phone, code: verificationCode, ...authClientContext() })
      });
      if (!result.token || !result.user) throw new Error("登录响应异常");
      setToken(result.token);
      onLogin(result.user);
    } catch (error) {
      message.error(error instanceof Error ? error.message : "登录失败");
    } finally {
      setLoading(false);
    }
  };

  const backToPhone = () => {
    setPhase("phone");
    resetCode();
  };

  return (
    <main className="studio-login">
      <section className="studio-login-hero studio-login-reveal" aria-labelledby="studio-title">
        <div className="studio-login-mark" aria-hidden="true"><span>径</span></div>
        <p className="studio-login-kicker">WENJING · HOME SERVICE OS</p>
        <h1 id="studio-title"><span className="studio-login-hero-line">让每一次上门服务，</span><em>都值得信赖。</em></h1>
        <p className="studio-login-intro">从客户预约、人员调度到服务交付，问径把家政团队的每一个关键动作连成一条清晰的工作流。</p>
        <div className="studio-login-proof" role="list" aria-label="平台能力">
          <div role="listitem"><strong>门店</strong><span>统一经营视图</span></div>
          <div role="listitem"><strong>人员</strong><span>安全协同与成长</span></div>
          <div role="listitem"><strong>订单</strong><span>履约全程可追踪</span></div>
        </div>
        <div className="studio-login-quote">
          <SafetyCertificateOutlined />
          <span>企业级数据隔离 · 手机号认证 · 多端同步</span>
        </div>
      </section>

      <section className="studio-login-panel studio-login-reveal" aria-label="登录面板">
        {realSmsTest ? <RealSmsTestLogin onLogin={onLogin} onBack={() => setRealSmsTest(false)} /> : <>
        <div className="studio-login-panel-head">
          <span className="studio-login-step">{phase === "phone" ? "01 / 身份确认" : "02 / 验证身份"}</span>
          <h2>{phase === "phone" ? "欢迎回来" : "输入验证码"}</h2>
          <p>{phase === "phone" ? "使用手机号进入你的家政工作台" : `验证码已发送至 ${phone}`}</p>
        </div>

        {phase === "phone" ? (
          <>
            <div className="studio-login-field">
              <label htmlFor="studio-phone">手机号</label>
              <Input id="studio-phone" size="large" prefix={<MobileOutlined />} value={phone} maxLength={11} inputMode="tel" autoComplete="tel" placeholder="请输入11位手机号" onChange={(event) => setPhone(event.target.value.replace(/\D/g, ""))} onPressEnter={() => void requestCode()} />
            </div>
            <div className="studio-login-quick"><div className="studio-login-quick-head"><span>登录角色</span><small>由登录账号自动识别</small></div></div>
            <Button type="primary" size="large" block loading={loading} icon={<ArrowRightOutlined />} className="studio-login-cta" onClick={() => void requestCode()}>获取验证码</Button>
            <Button size="large" block className="studio-login-real-entry" disabled={loading} icon={<MessageOutlined />} onClick={() => setRealSmsTest(true)}>真实短信测试登录</Button>
            <p className="studio-login-hint"><LockOutlined /> 试运行期间统一验证码为 <code>123456</code></p>
          </>
        ) : (
          <>
            <button type="button" className="studio-login-back" onClick={backToPhone}><ArrowLeftOutlined /> 返回修改手机号</button>
            <div className="studio-login-field studio-login-code-field">
              <label htmlFor="studio-code">6 位验证码</label>
              <Input.OTP id="studio-code" ref={otpRef} length={6} size="large" value={code} onChange={setCode} onInput={(cells) => { if (cells.length === 6 && cells.every(Boolean)) void verifyCode(cells.join("")); }} />
            </div>
            <Button type="primary" size="large" block loading={loading} icon={<CheckCircleFilled />} className="studio-login-cta" onClick={() => void verifyCode()}>验证并进入工作台</Button>
            <button type="button" className="studio-login-resend" disabled={countdown > 0 || loading} onClick={() => void requestCode()}>{countdown > 0 ? `${countdown} 秒后重新获取` : "重新获取验证码"}</button>
          </>
        )}
        </>}
        <p className="studio-login-footer">问径智慧家政 · 为值得托付的服务而生</p>
      </section>
    </main>
  );
}
